import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Activity, Shield } from 'lucide-react';

const ActivityLogs = () => {
  const { token, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchLogs();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE':
        return 'text-green-500';
      case 'UPDATE':
        return 'text-blue-500';
      case 'DELETE':
        return 'text-red-500';
      case 'EXPORT':
        return 'text-yellow-500';
      default:
        return 'text-muted-foreground';
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center p-8">
        <Shield className="mx-auto mb-4 text-destructive" size={48} />
        <h2 className="font-heading font-bold text-2xl mb-2">Access Denied</h2>
        <p className="text-muted-foreground font-body">Only administrators can view activity logs.</p>
      </div>
    );
  }

  return (
    <div data-testid="activity-logs-page">
      <div className="mb-8">
        <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight" data-testid="logs-title">Activity Logs</h1>
        <p className="text-muted-foreground font-body mt-2">Track all system activities and changes</p>
      </div>

      {/* Activity Timeline */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="activity-logs-container">
        {loading ? (
          <div className="text-center p-8 text-muted-foreground font-mono">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground font-body">No activity logs yet</div>
        ) : (
          <div className="space-y-4">
            {logs.map((log) => (
              <div
                key={log.id}
                className="flex gap-4 p-4 bg-secondary rounded-md border border-border hover:border-primary/50 transition-colors"
                data-testid={`log-entry-${log.id}`}
              >
                <div className="flex-shrink-0">
                  <Activity className={getActionColor(log.action)} size={20} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <span className={`font-mono text-xs font-semibold uppercase tracking-widest ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <span className="text-muted-foreground font-mono text-xs mx-2">•</span>
                      <span className="font-mono text-xs uppercase text-muted-foreground">{log.entity_type}</span>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="font-body text-sm mb-1">{log.details}</p>
                  <p className="font-mono text-xs text-muted-foreground">by {log.user_name}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;